import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Scale,
  Bell,
  MessageSquare,
  DollarSign,
  FileText,
  Briefcase,
  CreditCard,
} from "lucide-react";
import { StatusBadge } from "../partials/StatusBadge";
import SampleBadge from "../ui/SampleBadge";
import { useMatterInspector } from "../../contexts/MatterInspectorContext";
import { snakeCaseToTitleCase, practiceAreaColors } from "./utils";

interface MatterListWidgetProps {
  matters: any[];
  title: string;
  showDeadline?: boolean;
  emptyIcon?: React.FC<any>;
  emptyMessage?: string;
  limit?: number;
}

const MatterListWidget: React.FC<MatterListWidgetProps> = ({
  matters,
  title,
  showDeadline = true,
  emptyIcon: EmptyIcon = Briefcase,
  emptyMessage = "No matters to display",
  limit,
}) => {
  const navigate = useNavigate();
  const { openMatterInspector } = useMatterInspector();
  const displayMatters = limit ? matters.slice(0, limit) : matters;

  return (
    <section>
      <h2 className="text-xl font-bold mb-4" style={{ color: "#1F2937" }}>
        {title}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {displayMatters.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center col-span-2">
            <div className="h-16 w-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
              <EmptyIcon className="h-8 w-8 text-slate-300" />
            </div>
            <p className="font-semibold text-slate-700 mb-1">
              {emptyMessage}
            </p>
            <p className="text-sm text-slate-400 mb-4">
              Create your first matter to get started
            </p>
            <button
              onClick={() => navigate("/matters")}
              className="px-4 py-2 text-sm font-medium rounded-xl text-white"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              Go to Matters
            </button>
          </div>
        ) : (
          displayMatters.map((matter: any, index: number) => {
            const practiceArea = matter.practiceAreas?.[0] || "";
            const practiceAreaDisplay = practiceArea
              ? snakeCaseToTitleCase(practiceArea)
              : "";
            const displayStatus =
              matter.status === "open" ? "active" : matter.status;
            const lawyers = [
              matter.responsibleLawyerId?.name,
              ...(matter.assignedUserIds?.map((u: any) => u.name) || []),
            ].filter(Boolean);

            return (
              <motion.div
                key={matter._id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: index * 0.05,
                  duration: 0.3,
                }}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer"
                onClick={() => openMatterInspector(matter._id)}
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3
                          className="font-semibold"
                          style={{ color: "#1F2937" }}
                        >
                          {matter.title}
                        </h3>
                        {matter.isSample && <SampleBadge />}
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <User
                          className="h-4 w-4 flex-shrink-0"
                          style={{ color: "var(--color-accent)" }}
                          aria-hidden="true"
                        />
                        <p
                          className="text-sm font-medium"
                          style={{ color: "var(--color-accent)" }}
                        >
                          {matter.clientId?.displayName || ""}
                        </p>
                      </div>
                    </div>

                    <StatusBadge status={displayStatus}>
                      {displayStatus.charAt(0).toUpperCase() +
                        displayStatus.slice(1)}
                    </StatusBadge>
                  </div>

                  {practiceAreaDisplay && (
                    <div className="flex items-center gap-2 text-xs">
                      <span
                        className={`px-2 py-1 rounded-full font-medium ${
                          practiceAreaColors[practiceArea] ||
                          practiceAreaColors[practiceAreaDisplay] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {practiceAreaDisplay}
                      </span>
                    </div>
                  )}

                  {/* Lawyers */}
                  {lawyers.length > 0 && (
                    <div className="flex items-center gap-2 text-xs text-slate-600 pt-2 border-t border-slate-100">
                      <Scale
                        className="h-3.5 w-3.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className="font-medium">
                        {lawyers.join(", ")}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                    {showDeadline && matter.deadlineCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-xs font-medium">
                        <Bell className="h-3 w-3" aria-hidden="true" />
                        {matter.deadlineCount} deadline
                        {matter.deadlineCount > 1 ? "s" : ""}
                      </span>
                    )}

                    {matter.messageCount > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: "var(--color-primary-light)",
                          color: "var(--color-primary)",
                        }}
                      >
                        <MessageSquare
                          className="h-3 w-3"
                          aria-hidden="true"
                        />
                        {matter.messageCount} message
                        {matter.messageCount > 1 ? "s" : ""}
                      </span>
                    )}

                    {matter.trustStatus === "deficit" && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-xs font-medium">
                        <DollarSign
                          className="h-3 w-3"
                          aria-hidden="true"
                        />
                        Trust deficit
                      </span>
                    )}

                    {matter.documentCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                        <FileText
                          className="h-3 w-3"
                          aria-hidden="true"
                        />
                        {matter.documentCount} docs
                      </span>
                    )}

                    {matter.billing?.preferredPaymentMethod && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        ({
                          credit_card: "bg-blue-50 text-blue-700",
                          etransfer: "bg-purple-50 text-purple-700",
                          wire: "bg-indigo-50 text-indigo-700",
                          cheque: "bg-amber-50 text-amber-700",
                          eft: "bg-cyan-50 text-cyan-700",
                          cash: "bg-emerald-50 text-emerald-700",
                        } as Record<string, string>)[matter.billing.preferredPaymentMethod] || "bg-slate-100 text-slate-600"
                      }`}>
                        <CreditCard className="h-3 w-3" aria-hidden="true" />
                        {({
                          credit_card: "Credit Card",
                          etransfer: "e-Transfer",
                          wire: "Wire Transfer",
                          cheque: "Cheque",
                          eft: "EFT",
                          cash: "Cash",
                        } as Record<string, string>)[matter.billing.preferredPaymentMethod] || matter.billing.preferredPaymentMethod}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </section>
  );
};

export default MatterListWidget;
